import { useMemo } from "react";
import * as R from "ramda";
import cx from "classnames";
import uniqueId from "lodash.uniqueid";
import {
  sortDataPoints,
  fillInNulls,
  dataPointShape,
} from "../../../../Common/js/common/datacenterData.js";
import { area, line } from "d3-shape";
import parseISO from "date-fns/fp/parseISO";

import { arrayOf, bool, func, number, string } from "prop-types";

const propTypes = {
  color: string, // can use currentColor in css
  className: string,
  dataPoints: arrayOf(dataPointShape).isRequired,
  dottedLineForMissingData: bool,
  excludeUrl: string,
  height: number.isRequired,
  label: string,
  onExcludedHover: func,
  xScale: func.isRequired, // d3 scaleTime
  yScale: func.isRequired, // d3 scale
};

const TrendLine = (props) => {
  const {
    className,
    color,
    dataPoints,
    dottedLineForMissingData,
    excludeUrl,
    height,
    onExcludedHover,
    label,
    xScale,
    yScale,
    ...otherProps
  } = props;

  const lineGenerator = line()
    .x((d) => xScale(parseISO(d.timeFrame)))
    .y((d) => yScale(d.value))
    .defined((d) => d.value !== null);

  const areaGenerator = area()
    .x((d) => xScale(parseISO(d.timeFrame)))
    .y0(height)
    .y1((d) => yScale(d.value));

  // to be used with https://github.com/d3/d3-shape#line_defined
  const dataFilledWithNulls = R.compose(
    sortDataPoints,
    R.when(() => dottedLineForMissingData, fillInNulls),
    R.reject(R.propEq("exclude", true)),
  )(dataPoints);

  const processedDataPoints = sortDataPoints(dataPoints);

  const dataPointsNoExlcuded = R.compose(R.reject(R.propEq("exclude", true)))(
    processedDataPoints,
  );

  const lineD = lineGenerator(dataFilledWithNulls);
  const areaD = areaGenerator(dataPointsNoExlcuded);

  /* render missing data lines */
  const renderMissingDataLines = () => {
    // only render this if we need to
    if (!dottedLineForMissingData) {
      return null;
    }

    const dataByTimeframe = R.indexBy(
      R.prop("timeFrame"),
      dataPointsNoExlcuded,
    );

    // a list of pairs of year ranges that are missing
    const missingData = R.compose(
      R.map(R.map((year) => dataByTimeframe[year])),
      R.filter((years) => years[0] + 1 !== years[1]),
      R.aperture(2),
      R.pluck("timeFrame"),
    )(dataPointsNoExlcuded);

    return (
      <g className="trend-line__missing-data">
        {missingData.map((pair, i) => {
          return (
            <path
              key={i}
              className="trend-line__missing-data-line"
              d={lineGenerator(pair)}
            />
          );
        })}
      </g>
    );
  };

  /* render excluded line */
  const renderExcluded = () => {
    const excludedPoints = R.filter(R.propEq("exclude", true), dataPoints);

    // if we don't have any excluded points, bail!
    if (excludedPoints.length === 0) {
      return;
    }

    // get the excluded points and all the points around it
    const excludedLineData = R.compose(sortDataPoints, (array) =>
      array.reduce((acc, d, i) => {
        const excluded = (d) => d && d.exclude === true;

        if (excluded(d) || excluded(array[i - 1]) || excluded(array[i + 1])) {
          return acc.concat(d);
        } else {
          return acc;
        }
      }, []),
    )(dataPoints);

    return (
      <g className="trend-line__excluded">
        <path
          className="trend-line__excluded-line"
          d={lineGenerator(excludedLineData)}
        />
        {excludedPoints.map((point, i) => {
          const hitAreaCircle = (
            <circle
              className="trend-line__excluded-circle-hit-area"
              r="10"
              style={{ fill: "red", opacity: 0, cursor: "pointer" }}
              cx={xScale(parseISO(point.timeFrame))}
              cy={yScale(point.value)}
              onMouseEnter={(e) => {
                onExcludedHover &&
                  onExcludedHover({
                    top: yScale(point.value),
                    left: xScale(parseISO(point.timeFrame)),
                    children: "Excluded",
                  });
              }}
              onMouseLeave={(e) => onExcludedHover && onExcludedHover()}
            />
          );

          return (
            <g key={i}>
              {/* if we have an excludeUrl, wrap the circle in an anchor link */}
              {excludeUrl ? (
                <a href={excludeUrl} target="_blank" rel="noopener noreferrer">
                  {hitAreaCircle}
                </a>
              ) : (
                hitAreaCircle
              )}
              <circle
                r="4"
                className="trend-line__excluded-circle"
                cx={xScale(parseISO(point.timeFrame))}
                cy={yScale(point.value)}
                style={{
                  pointerEvents: "none", // so the hit area is used instead
                }}
              />
            </g>
          );
        })}
      </g>
    );
  };

  const gradientId = useMemo(() => uniqueId("linear-gradient-"), []);

  return (
    <g
      className={cx("trend-line", className)}
      style={{ color }}
      {...otherProps}
    >
      <defs>
        <linearGradient
          className="trend-line__linear-gradient"
          id={gradientId}
          x1="0%"
          x2="0%"
          y1="0%"
          y2="100%"
        >
          <stop
            className="start"
            offset="0%"
            stopColor="currentColor"
            stopOpacity="0.6"
          ></stop>
          <stop
            className="end"
            offset="80%"
            stopColor="currentColor"
            stopOpacity="0"
          ></stop>
        </linearGradient>
      </defs>

      <path
        className="trend-line__area"
        d={areaD}
        fill={`url(#${gradientId})`}
        style={{
          pointerEvents: "none", // in case there is something underneith (like excluded dot)
        }}
      />
      <path
        className="trend-line__line"
        d={lineD}
        style={{
          pointerEvents: "none", // in case there is something underneith (like excluded dot)
        }}
      />

      <g className="trend-line__data-points">
        {dataFilledWithNulls.map((point) => {
          const x = xScale(parseISO(point.timeFrame));
          const y = yScale(point.value);
          return (
            <g
              key={point.timeFrame}
              className="trend-line__data-point"
              transform={`translate(${x} ${y})`} // for IE
              style={{
                transform: `translate(${x}px, ${y}px)`,
              }}
            >
              <circle className="trend-line__data-point-circle" r="2" />
              {/* <text className="trend-line__data-point-text">
                   {point.displayValue}
                 </text> */}
            </g>
          );
        })}
      </g>

      {label && (
        <text
          className="trend-line__label"
          x={xScale(xScale.domain()[1]) + 6}
          y={yScale(R.compose(R.prop("value"), R.last)(processedDataPoints))}
          dy={"0.4em"}
        >
          {label}
        </text>
      )}
      {/* excluded rendered on top so the area doesn't block the cursor pointer */}
      {renderExcluded()}

      {renderMissingDataLines()}
    </g>
  );
};

TrendLine.propTypes = propTypes;
export default TrendLine;
